import React, { useState, useEffect, useCallback } from "react";
import {
  HelpCircle,
  Layers,
  FileText,
  CheckCircle,
  Search,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  Sparkles,
  Send,
  Eye,
  Trash2,
  Filter,
  Loader2,
} from "lucide-react";
import api from "../api/client";

const INTENT_COLORS = {
  informational: "bg-blue-500/20 text-blue-400",
  commercial: "bg-amber-500/20 text-amber-400",
  transactional: "bg-green-500/20 text-green-400",
  navigational: "bg-purple-500/20 text-purple-400",
};

const STATUS_COLORS = {
  pending: "bg-gray-500/20 text-gray-400",
  generating: "bg-yellow-500/20 text-yellow-400",
  draft: "bg-blue-500/20 text-blue-400",
  reviewing: "bg-purple-500/20 text-purple-400",
  approved: "bg-cyan-500/20 text-cyan-400",
  publishing: "bg-amber-500/20 text-amber-400",
  published: "bg-green-500/20 text-green-400",
  failed: "bg-red-500/20 text-red-400",
};

export default function PAADashboard({ selectedSite }) {
  const [activeTab, setActiveTab] = useState("overview");
  const [dashboard, setDashboard] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [clusters, setClusters] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scraping, setScraping] = useState(false);
  const [clustering, setClustering] = useState(false);
  const [filters, setFilters] = useState({ intent: "", status: "", keyword: "", category: "" });
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [expandedCluster, setExpandedCluster] = useState(null);

  const siteId = selectedSite?.id;

  const fetchDashboard = useCallback(async () => {
    if (!siteId) return;
    try {
      const res = await api.get(`/websites/${siteId}/paa/dashboard/`);
      setDashboard(res.data);
    } catch { /* ignore */ }
  }, [siteId]);

  const fetchQuestions = useCallback(async () => {
    if (!siteId) return;
    const params = new URLSearchParams();
    if (filters.intent) params.append("intent", filters.intent);
    if (filters.status) params.append("status", filters.status);
    if (filters.keyword) params.append("keyword", filters.keyword);
    if (filters.category) params.append("category", filters.category);
    params.append("page_size", "100");
    try {
      const res = await api.get(`/websites/${siteId}/paa/questions/?${params}`);
      setQuestions(res.data.results || []);
    } catch { /* ignore */ }
  }, [siteId, filters]);

  const fetchClusters = useCallback(async () => {
    if (!siteId) return;
    try {
      const res = await api.get(`/websites/${siteId}/paa/clusters/`);
      setClusters(res.data || []);
    } catch { /* ignore */ }
  }, [siteId]);

  const fetchCategories = useCallback(async () => {
    if (!siteId) return;
    try {
      const res = await api.get(`/websites/${siteId}/paa/categories/`);
      setCategories(res.data || []);
    } catch { /* ignore */ }
  }, [siteId]);

  useEffect(() => {
    setSelectedQuestions([]);
    setExpandedCluster(null);
  }, [siteId]);

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchDashboard(), fetchQuestions(), fetchClusters(), fetchCategories()])
      .finally(() => setLoading(false));
  }, [fetchDashboard, fetchQuestions, fetchClusters, fetchCategories]);

  const handleScrape = async () => {
    setScraping(true);
    try {
      await api.post(`/websites/${siteId}/paa/scrape/`, {});
      setTimeout(() => {
        fetchDashboard();
        fetchQuestions();
        setScraping(false);
      }, 5000);
    } catch {
      setScraping(false);
    }
  };

  const handleAutoCluster = async () => {
    setClustering(true);
    try {
      await api.post(`/websites/${siteId}/paa/auto-cluster/`, {});
      setTimeout(() => {
        fetchClusters();
        fetchDashboard();
        setClustering(false);
      }, 8000);
    } catch {
      setClustering(false);
    }
  };

  const handleCreateCluster = async () => {
    if (selectedQuestions.length < 1) return;
    try {
      await api.post(`/websites/${siteId}/paa/clusters/create/`, {
        primary_question_id: selectedQuestions[0],
        related_question_ids: selectedQuestions.slice(1),
      });
      setSelectedQuestions([]);
      fetchClusters();
      fetchDashboard();
    } catch (err) {
      const msg = err?.response?.data?.error || err?.response?.data?.detail || "Failed to create cluster. Make sure the selected questions belong to the current website.";
      window.alert(msg);
    }
  };

  const handleGenerate = async (clusterId, contentType = "blog_post") => {
    try {
      await api.post(`/websites/${siteId}/paa/clusters/${clusterId}/generate/`, {
        content_type: contentType,
      });
      fetchClusters();
    } catch (err) {
      const msg = err?.response?.data?.error || err?.response?.data?.detail || "Failed to start generation.";
      window.alert(msg);
    }
  };

  const handlePublish = async (clusterId) => {
    try {
      await api.post(`/websites/${siteId}/paa/clusters/${clusterId}/publish/`);
      fetchClusters();
    } catch (err) {
      const msg = err?.response?.data?.error || err?.response?.data?.detail || "Failed to publish cluster.";
      window.alert(msg);
    }
  };

  const handleDeleteCluster = async (clusterId) => {
    try {
      await api.delete(`/websites/${siteId}/paa/clusters/${clusterId}/`);
      fetchClusters();
      fetchDashboard();
    } catch { /* ignore */ }
  };

  const toggleQuestion = (id) => {
    setSelectedQuestions((prev) =>
      prev.includes(id) ? prev.filter((q) => q !== id) : [...prev, id]
    );
  };

  if (!siteId) {
    return <div className="text-gray-400 p-8 text-center">Select a site to view PAA data.</div>;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin text-copper" />
      </div>
    );
  }

  const tabs = [
    { id: "overview", label: "Overview", icon: HelpCircle },
    { id: "questions", label: "Questions", icon: Search },
    { id: "clusters", label: "Clusters", icon: Layers },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">PAA Pipeline</h1>
          <p className="text-sm text-gray-400 mt-1">
            People Also Ask questions → Blog content pipeline
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleScrape}
            disabled={scraping}
            className="flex items-center gap-2 px-4 py-2 bg-copper hover:bg-copper-600 text-white rounded-lg text-sm font-medium disabled:opacity-50"
          >
            {scraping ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            {scraping ? "Scraping..." : "Scrape PAA"}
          </button>
          <button
            onClick={handleAutoCluster}
            disabled={clustering}
            className="flex items-center gap-2 px-4 py-2 bg-dark-200 hover:bg-dark-100 text-white rounded-lg text-sm font-medium border border-dark-50 disabled:opacity-50"
          >
            {clustering ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {clustering ? "Clustering..." : "Auto-Cluster"}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-dark-300 p-1 rounded-lg w-fit">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === tab.id
                ? "bg-dark-100 text-white"
                : "text-gray-400 hover:text-white"
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === "overview" && dashboard && (
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {[
              { label: "Total Questions", value: dashboard.total_questions, icon: HelpCircle, color: "text-blue-400" },
              { label: "Unclustered", value: dashboard.unclustered_questions, icon: Search, color: "text-amber-400" },
              { label: "Has Content", value: dashboard.with_existing_content, icon: FileText, color: "text-purple-400" },
              { label: "Drafts Generated", value: dashboard.drafts_generated, icon: Layers, color: "text-cyan-400" },
              { label: "Published", value: dashboard.published, icon: CheckCircle, color: "text-green-400" },
            ].map((card) => (
              <div key={card.label} className="bg-dark-200 border border-dark-50 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <card.icon className={`w-4 h-4 ${card.color}`} />
                  <span className="text-xs text-gray-400">{card.label}</span>
                </div>
                <div className="text-2xl font-bold text-white">{card.value}</div>
              </div>
            ))}
          </div>

          {/* Intent Breakdown */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-dark-200 border border-dark-50 rounded-xl p-5">
              <h3 className="text-sm font-semibold text-white mb-4">Intent Breakdown</h3>
              <div className="space-y-3">
                {Object.entries(dashboard.intent_breakdown || {}).map(([intent, count]) => (
                  <div key={intent} className="flex items-center justify-between">
                    <span className={`text-xs px-2 py-1 rounded-full ${INTENT_COLORS[intent] || "bg-gray-500/20 text-gray-400"}`}>
                      {intent}
                    </span>
                    <span className="text-white font-medium">{count}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-dark-200 border border-dark-50 rounded-xl p-5">
              <h3 className="text-sm font-semibold text-white mb-4">Cluster Status</h3>
              <div className="space-y-3">
                {Object.entries(dashboard.clusters_by_status || {}).map(([st, count]) => (
                  <div key={st} className="flex items-center justify-between">
                    <span className={`text-xs px-2 py-1 rounded-full ${STATUS_COLORS[st] || "bg-gray-500/20 text-gray-400"}`}>
                      {st}
                    </span>
                    <span className="text-white font-medium">{count}</span>
                  </div>
                ))}
                {Object.keys(dashboard.clusters_by_status || {}).length === 0 && (
                  <p className="text-gray-500 text-sm">No clusters yet</p>
                )}
              </div>
            </div>
          </div>

          {/* Top Questions */}
          {dashboard.top_questions?.length > 0 && (
            <div className="bg-dark-200 border border-dark-50 rounded-xl p-5">
              <h3 className="text-sm font-semibold text-white mb-4">Top Unclustered Questions</h3>
              <div className="space-y-2">
                {dashboard.top_questions.map((q) => (
                  <div key={q.id} className="flex items-center justify-between py-2 border-b border-dark-50 last:border-0">
                    <div className="flex-1">
                      <p className="text-sm text-white">{q.question_text}</p>
                      <p className="text-xs text-gray-500 mt-1">from: {q.source_keyword}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${INTENT_COLORS[q.intent]}`}>
                        {q.intent}
                      </span>
                      <span className="text-xs text-gray-400 w-8 text-right">{q.priority_score}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Questions Tab */}
      {activeTab === "questions" && (
        <div className="space-y-4">
          {/* Filters */}
          <div className="flex flex-wrap gap-3 items-center">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-400" />
            </div>
            <select
              value={filters.intent}
              onChange={(e) => setFilters((f) => ({ ...f, intent: e.target.value }))}
              className="bg-dark-200 border border-dark-50 rounded-lg px-3 py-2 text-sm text-white"
            >
              <option value="">All Intents</option>
              <option value="informational">Informational</option>
              <option value="commercial">Commercial</option>
              <option value="transactional">Transactional</option>
              <option value="navigational">Navigational</option>
            </select>
            <select
              value={filters.status}
              onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
              className="bg-dark-200 border border-dark-50 rounded-lg px-3 py-2 text-sm text-white"
            >
              <option value="">All Status</option>
              <option value="unclustered">Unclustered</option>
              <option value="draft_generated">Draft Generated</option>
              <option value="published">Published</option>
              <option value="has_content">Has Existing Content</option>
            </select>
            {categories.length > 0 && (
              <select
                value={filters.category}
                onChange={(e) => setFilters((f) => ({ ...f, category: e.target.value }))}
                className="bg-dark-200 border border-dark-50 rounded-lg px-3 py-2 text-sm text-white"
              >
                <option value="">All Categories</option>
                {categories.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            )}
            <input
              type="text"
              value={filters.keyword}
              onChange={(e) => setFilters((f) => ({ ...f, keyword: e.target.value }))}
              placeholder="Search questions..."
              className="bg-dark-200 border border-dark-50 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 w-64"
            />
            {selectedQuestions.length > 0 && (
              <button
                onClick={handleCreateCluster}
                className="flex items-center gap-2 px-3 py-2 bg-copper hover:bg-copper-600 text-white rounded-lg text-sm font-medium"
              >
                <Layers className="w-4 h-4" />
                Cluster {selectedQuestions.length} Selected
              </button>
            )}
          </div>

          {/* Questions Table */}
          <div className="bg-dark-200 border border-dark-50 rounded-xl overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-dark-50">
                  <th className="p-3 text-left w-8">
                    <input
                      type="checkbox"
                      className="rounded bg-dark-300 border-dark-50"
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedQuestions(questions.map((q) => q.id));
                        } else {
                          setSelectedQuestions([]);
                        }
                      }}
                    />
                  </th>
                  <th className="p-3 text-left text-xs font-medium text-gray-400 uppercase">Question</th>
                  <th className="p-3 text-left text-xs font-medium text-gray-400 uppercase">Source</th>
                  <th className="p-3 text-left text-xs font-medium text-gray-400 uppercase">Category</th>
                  <th className="p-3 text-left text-xs font-medium text-gray-400 uppercase">Intent</th>
                  <th className="p-3 text-right text-xs font-medium text-gray-400 uppercase">Score</th>
                  <th className="p-3 text-center text-xs font-medium text-gray-400 uppercase">Status</th>
                </tr>
              </thead>
              <tbody>
                {questions.map((q) => (
                  <tr key={q.id} className="border-b border-dark-50/50 hover:bg-dark-100/50">
                    <td className="p-3">
                      <input
                        type="checkbox"
                        checked={selectedQuestions.includes(q.id)}
                        onChange={() => toggleQuestion(q.id)}
                        className="rounded bg-dark-300 border-dark-50"
                      />
                    </td>
                    <td className="p-3">
                      <p className="text-sm text-white">{q.question_text}</p>
                    </td>
                    <td className="p-3 text-xs text-gray-400 max-w-32 truncate">{q.source_keyword}</td>
                    <td className="p-3 text-xs text-gray-400">{q.service_category || "—"}</td>
                    <td className="p-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${INTENT_COLORS[q.intent]}`}>
                        {q.intent}
                      </span>
                    </td>
                    <td className="p-3 text-right">
                      <span className={`text-sm font-medium ${q.priority_score >= 70 ? "text-green-400" : q.priority_score >= 40 ? "text-amber-400" : "text-gray-400"}`}>
                        {q.priority_score}
                      </span>
                    </td>
                    <td className="p-3 text-center">
                      {q.published ? (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-green-500/20 text-green-400">Published</span>
                      ) : q.draft_generated ? (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-400">Draft</span>
                      ) : q.has_existing_content ? (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-400">Has Content</span>
                      ) : (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-gray-500/20 text-gray-400">New</span>
                      )}
                    </td>
                  </tr>
                ))}
                {questions.length === 0 && (
                  <tr>
                    <td colSpan={7} className="p-8 text-center text-gray-500">
                      No PAA questions found. Click "Scrape PAA" to discover questions.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Clusters Tab */}
      {activeTab === "clusters" && (
        <div className="space-y-4">
          {clusters.length === 0 ? (
            <div className="bg-dark-200 border border-dark-50 rounded-xl p-8 text-center">
              <Layers className="w-8 h-8 text-gray-500 mx-auto mb-3" />
              <p className="text-gray-400">No clusters yet. Use "Auto-Cluster" or select questions to create clusters manually.</p>
            </div>
          ) : (
            clusters.map((cluster) => (
              <div key={cluster.id} className="bg-dark-200 border border-dark-50 rounded-xl overflow-hidden">
                {/* Cluster Header */}
                <div
                  className="flex items-center justify-between p-4 cursor-pointer hover:bg-dark-100/50"
                  onClick={() => setExpandedCluster(expandedCluster === cluster.id ? null : cluster.id)}
                >
                  <div className="flex items-center gap-3 flex-1">
                    {expandedCluster === cluster.id ? (
                      <ChevronDown className="w-4 h-4 text-gray-400" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                    )}
                    <div>
                      <h3 className="text-sm font-medium text-white">
                        {cluster.draft_title || cluster.primary_question_text}
                      </h3>
                      <p className="text-xs text-gray-400 mt-1">
                        {cluster.question_count} questions
                        {cluster.content_score ? ` · Score: ${cluster.content_score}` : ""}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-2 py-1 rounded-full ${STATUS_COLORS[cluster.draft_status]}`}>
                      {cluster.draft_status}
                    </span>
                    {cluster.content_type && (
                      <span className="text-[10px] uppercase px-1.5 py-0.5 rounded bg-dark-100 text-gray-400">
                        {cluster.content_type === "landing_page" ? "Landing" : "Blog"}
                      </span>
                    )}
                    {cluster.draft_status === "pending" && (
                      <>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleGenerate(cluster.id, "blog_post"); }}
                          className="flex items-center gap-1 px-2 py-1 hover:bg-dark-100 rounded-lg text-copper text-xs"
                          title="Generate Blog Post draft"
                        >
                          <Sparkles className="w-3.5 h-3.5" />
                          Blog Post
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleGenerate(cluster.id, "landing_page"); }}
                          className="flex items-center gap-1 px-2 py-1 hover:bg-dark-100 rounded-lg text-cyan-400 text-xs"
                          title="Generate Landing Page draft"
                        >
                          <FileText className="w-3.5 h-3.5" />
                          Landing Page
                        </button>
                      </>
                    )}
                    {(cluster.draft_status === "draft" || cluster.draft_status === "approved") && (
                      <button
                        onClick={(e) => { e.stopPropagation(); handlePublish(cluster.id); }}
                        className="p-1.5 hover:bg-dark-100 rounded-lg text-green-400"
                        title="Publish to WordPress"
                      >
                        <Send className="w-4 h-4" />
                      </button>
                    )}
                    {cluster.published_url && (
                      <a
                        href={cluster.published_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="p-1.5 hover:bg-dark-100 rounded-lg text-blue-400"
                        title="View Published Post"
                      >
                        <Eye className="w-4 h-4" />
                      </a>
                    )}
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDeleteCluster(cluster.id); }}
                      className="p-1.5 hover:bg-dark-100 rounded-lg text-red-400"
                      title="Delete Cluster"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Expanded Content */}
                {expandedCluster === cluster.id && (
                  <div className="border-t border-dark-50 p-4 space-y-4">
                    {/* Questions in cluster */}
                    <div>
                      <h4 className="text-xs font-medium text-gray-400 uppercase mb-2">Questions</h4>
                      <div className="space-y-1">
                        <div className="flex items-center gap-2 text-sm">
                          <span className="text-xs bg-copper/20 text-copper px-1.5 py-0.5 rounded">Primary</span>
                          <span className="text-white">{cluster.primary_question_text}</span>
                        </div>
                        {cluster.related_questions_detail?.map((rq) => (
                          <div key={rq.id} className="flex items-center gap-2 text-sm pl-6">
                            <span className="text-gray-400">{rq.question_text}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Draft preview */}
                    {cluster.draft_content && (
                      <div>
                        <h4 className="text-xs font-medium text-gray-400 uppercase mb-2">Draft Preview</h4>
                        <div
                          className="bg-dark-300 rounded-lg p-4 text-sm text-gray-300 max-h-[600px] overflow-y-auto prose prose-invert prose-sm"
                          dangerouslySetInnerHTML={{
                            __html: cluster.draft_content,
                          }}
                        />
                      </div>
                    )}

                    {/* Error message */}
                    {/* Published URL */}                    {cluster.published_url && (                      <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-3 flex items-center justify-between">                        <div>                          <p className="text-xs text-green-400 font-medium">Published to WordPress</p>                          <a href={cluster.published_url} target="_blank" rel="noopener noreferrer" className="text-sm text-green-300 hover:underline">{cluster.published_url}</a>                        </div>                        <a href={cluster.published_url} target="_blank" rel="noopener noreferrer" className="p-2 bg-green-500/20 rounded-lg hover:bg-green-500/30">                          <Eye className="w-4 h-4 text-green-400" />                        </a>                      </div>                    )}
                    {cluster.error_message && (
                      <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3">
                        <p className="text-xs text-red-400">{cluster.error_message}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
